import React, {
  PropsWithChildren,
  useContext,
  type FunctionComponent,
} from 'react';

import { formatCurrency } from '@/domains/core/localization/currency';
import { getCurrency, getLocale } from '@/domains/core/settings/utils';
import { CheckCircle } from '@/domains/core/tamagoshi/icons/CheckCircle';
import { Text } from '@/domains/core/tamagoshi/Text/Text';
import { CouponContext } from '@/domains/loyalty/components/Coupon/CouponContext';
import { formatPercentageNarrowedByLocale } from '@/domains/loyalty/utils/numbers';

import {
  line1Translation,
  line2Translation,
  line3Translation,
  termsLine1,
  termsLine2,
  termsLine3,
  termsLineRestrictionText,
  useIndication,
  userCouponsAmountContent,
  userCouponsAmountTitle,
  userCouponsConditionsCatalogue,
  userCouponsConditionsDate,
  userCouponsConditionsNonCombinable,
  userCouponsConditionsTitle,
  userCouponsHowToContent,
  userCouponsHowToTitle,
} from './translations';

import styles from './CouponModal.module.scss';

const CONTEXTS_WITH_SIMPLE_CONDITIONS = ['product page', 'A2C', 'cart page'];
const CONTEXTS_WITH_BUNDLE_CONDITIONS = ['bundle'];
const CONTEXTS_WITH_DETAILED_CONDITIONS = [
  'my_advantages_space',
  'highlighted_coupon',
];

const TermsLine: React.FC<PropsWithChildren> = ({ children }) => (
  <div className={styles.termsLine}>{children}</div>
);

const TermsList: React.FC<PropsWithChildren> = ({ children }) => (
  <ul className={styles.termsSectionList}>{children}</ul>
);

const TermsListItem: React.FC<PropsWithChildren> = ({ children }) => (
  <li className={styles.termsSectionListItem}>
    <CheckCircle className={styles.icon} />
    {children}
  </li>
);

const SimpleCouponConditions: React.FC = () => (
  <>
    <TermsLine>{termsLineRestrictionText}</TermsLine>
    <TermsLine>{termsLine1}</TermsLine>
    <TermsLine>{termsLine2}</TermsLine>
    <TermsLine>{termsLine3}</TermsLine>
  </>
);

const BundleCouponConditions: React.FC = () => (
  <>
    <TermsLine>{line1Translation}</TermsLine>
    <TermsLine>{line2Translation}</TermsLine>
    <TermsLine>{line3Translation}</TermsLine>
  </>
);

const DetailedCouponConditions: React.FC = () => {
  const { amount, percent, minPurchase, validUntil } =
    useContext(CouponContext);
  return (
    <>
      <Text
        tag="h2"
        type="body1"
        weight="bold"
        className={styles.termsSectionTitle}
      >
        {userCouponsAmountTitle.format({
          amount: percent
            ? formatPercentageNarrowedByLocale(percent)
            : formatCurrency(amount ?? 0, getCurrency(), {
                minimumFractionDigits: 0,
              }),
        })}
      </Text>
      <Text tag="p" className={styles.termsSectionContent}>
        {userCouponsAmountContent.format({
          minPurchase: formatCurrency(minPurchase ?? 0, getCurrency(), {
            minimumFractionDigits: 0,
          }),
        })}
      </Text>
      <Text
        tag="h2"
        type="body1"
        weight="bold"
        className={styles.termsSectionTitle}
      >
        {userCouponsHowToTitle}
      </Text>
      <Text tag="p" className={styles.termsSectionContent}>
        {userCouponsHowToContent}
      </Text>
      <Text
        tag="h2"
        type="body1"
        weight="bold"
        className={styles.termsSectionTitle}
      >
        {userCouponsConditionsTitle}
      </Text>
      <TermsList>
        <TermsListItem>
          <Text tag="span" type="body2">
            {userCouponsConditionsDate.format({
              validUntil: new Date(validUntil ?? '').toLocaleDateString(
                getLocale(),
              ),
            })}
          </Text>
        </TermsListItem>
        <TermsListItem>
          <Text tag="span" type="body2">
            {userCouponsConditionsCatalogue}
          </Text>
        </TermsListItem>
        <TermsListItem>
          <Text tag="span" type="body2">
            {userCouponsConditionsNonCombinable}
          </Text>
        </TermsListItem>
      </TermsList>
    </>
  );
};

export const CouponModalContent: FunctionComponent = () => {
  const { context } = useContext(CouponContext);
  if (CONTEXTS_WITH_SIMPLE_CONDITIONS.includes(context))
    return <SimpleCouponConditions />;
  if (CONTEXTS_WITH_BUNDLE_CONDITIONS.includes(context))
    return <BundleCouponConditions />;
  if (CONTEXTS_WITH_DETAILED_CONDITIONS.includes(context)) {
    return <DetailedCouponConditions />;
  }
  return <TermsLine>{useIndication}</TermsLine>;
};

CouponModalContent.displayName = 'CouponModalContent';
